import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { useParams, Link as RouterLink } from 'react-router';
import { NoAccessDialog } from '../../operator-logins/components/NoAccessDialog.tsx';
import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../../layout/components/form/FormLayout.tsx';
import { Box, Button, Link } from '@mui/material';
import { Public, VerifiedUser } from '@mui/icons-material';
import { OperatorRoutedModalContent } from '../../layout/components/OperatorRoutedModalContent.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import { SingleZoneView } from '../../zones/components/ZoneMapView.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    OnstreetZoneDetailDTO,
    PermitType,
    TariffContextType,
} from './Zones.tsx';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    LabeledSignalisationDownload,
    SignalisationType,
} from '../../common/components/LabeledSignalisationDownload.tsx';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { DateTime } from 'luxon';
import { LuxonDateStringFormats } from 'dg-web-shared/lib/LuxonDateStringFormats.ts';

export function ZonesDetail() {
    const urlParams = useParams<{ zoneId: string }>();
    const zoneId = urlParams.zoneId ? parseInt(urlParams.zoneId, 10) : null;
    const [zoneDetailState] = useServerFetch<
        OnstreetZoneDetailDTO,
        { zoneId: number }
    >(
        ({ zoneId }) => ({
            url: `/ui-api/operator-account/onstreet-zones/${zoneId}`,
        }),

        zoneId ? { zoneId: zoneId } : null,
    );

    return (
        <OperatorRoutedModal
            variant={ModalVariant.BIG}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={zoneDetailState}
                    render={zoneDetail => {
                        if (zoneDetail) {
                            return (
                                <Details
                                    zoneDetail={zoneDetail}
                                    onClose={controller.close}
                                />
                            );
                        } else {
                            return (
                                <NoAccessDialog
                                    open
                                    onClose={controller.close}
                                />
                            );
                        }
                    }}
                />
            )}
        />
    );
}

function Details({
    zoneDetail,
    onClose,
}: {
    zoneDetail: OnstreetZoneDetailDTO;
    onClose: () => void;
}) {
    const { operatorExternalId } = useOperatorContext();

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={zoneDetail.zoneName}
                    editMode={false}
                    buttons={
                        <Button variant="outlined" onClick={onClose}>
                            <Localized
                                de="Schliessen"
                                fr="Fermer"
                                it="Chiudi"
                                en="Close"
                            />
                        </Button>
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        {' '}
                        <ReadOnlyTextField
                            label={'Nr'}
                            value={zoneDetail.zoneCode}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zonenname"
                                    fr="Nom zones"
                                    it="Nome zone"
                                    en="Zones name"
                                />
                            }
                            value={zoneDetail.zoneName}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ort"
                                    fr="Localité"
                                    it="Località"
                                    en="Location"
                                />
                            }
                            value={`${zoneDetail.zipCode} ${zoneDetail.city}`}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zutritt"
                                    fr="Accès"
                                    it="Accesso"
                                    en="Access"
                                />
                            }
                            value={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {zoneDetail.needsPermit ? (
                                        <>
                                            <VerifiedUser sx={iconSx} />
                                            <Localized
                                                de="Privat"
                                                fr="Privé"
                                                it="Privato"
                                                en="Private"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Public sx={iconSx} />
                                            <Localized
                                                de="Öffentlich"
                                                fr="Public"
                                                it="Pubblico"
                                                en="Public"
                                            />
                                        </>
                                    )}
                                </Box>
                            }
                        />
                        <ZoneTariffFields zoneDetail={zoneDetail} />
                    </FormLeftColumn>
                    <FormRightColumn>
                        {Boolean(zoneDetail.geodataText) && (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '400px',
                                }}
                            >
                                <SingleZoneView
                                    zone={{
                                        id: zoneDetail.zoneId,
                                        externalName: zoneDetail.zoneName,
                                        geodataText: zoneDetail.geodataText,
                                    }}
                                />
                            </Box>
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="MWST-Nummer"
                                    fr="Numéro de TVA"
                                    it="Numero IVA"
                                    en="VAT number"
                                />
                            }
                            value={zoneDetail.vatNumber ?? EmDash}
                        />
                        {zoneDetail.showAccount && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Gruppe"
                                        fr="Groupe"
                                        it="Gruppo"
                                        en="Group"
                                    />
                                }
                                value={
                                    zoneDetail.dtaAccountDescription ?? EmDash
                                }
                            />
                        )}
                        {zoneDetail.signalisationPdfOnstreet && (
                            <LabeledSignalisationDownload
                                signalisationPdfId={
                                    zoneDetail.signalisationPdfOnstreet
                                }
                                type={SignalisationType.ZONE}
                            />
                        )}
                        <ZonePermitTypesField
                            permitTypes={zoneDetail.permitTypes}
                            operatorExternalId={operatorExternalId}
                        />
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function ZoneTariffFields({
    zoneDetail,
}: {
    zoneDetail: OnstreetZoneDetailDTO;
}) {
    const { operatorExternalId } = useOperatorContext();
    const hasSeparateTariffs = zoneDetail.tariffs.length > 1;

    return (
        <>
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Tarife"
                        fr="Tarifs"
                        it="Tariffe"
                        en="Tariffs"
                    />
                }
                value={
                    zoneDetail.tariffs.length > 0
                        ? zoneDetail.tariffs.map(tariffType => (
                              <div
                                  key={`${tariffType.tariffId}-${tariffType.contextType}`}
                              >
                                  <Link
                                      component={RouterLink}
                                      to={`/${operatorExternalId}/tariffs/${tariffType.tariffId}`}
                                  >
                                      {tariffType.tariffName}
                                  </Link>
                                  {hasSeparateTariffs &&
                                      ` (${tariffType.contextType === TariffContextType.TOM ? 'TOMeco' : 'digital'})`}
                              </div>
                          ))
                        : EmDash
                }
            />
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Gültig seit"
                        fr="Valable depuis"
                        it="Valido dal"
                        en="Valid since"
                    />
                }
                value={
                    zoneDetail.tariffsValidFrom
                        ? DateTime.fromISO(
                              zoneDetail.tariffsValidFrom,
                          ).toFormat(LuxonDateStringFormats.DATE)
                        : EmDash
                }
            />
        </>
    );
}

function ZonePermitTypesField({
    permitTypes,
    operatorExternalId,
}: {
    permitTypes: PermitType[];
    operatorExternalId: string;
}) {
    return (
        <ReadOnlyTextField
            label={
                <Localized
                    de="Bewilligungsarten"
                    fr="Types d'autorisation"
                    it="Tipi di autorizzazione"
                    en="Permit types"
                />
            }
            value={
                permitTypes.length > 0
                    ? permitTypes.map(permitType => (
                          <div key={permitType.permitId}>
                              <Link
                                  component={RouterLink}
                                  to={`/${operatorExternalId}/permit-types/${permitType.permitId}`}
                              >
                                  {permitType.permitName}
                              </Link>
                          </div>
                      ))
                    : EmDash
            }
        />
    );
}

const iconSx = {
    height: 19,
    paddingRight: 0.5,
    marginTop: -0.5,
};
